// Match-rate widgets — types + shaping (Phase 3, half-donut type).
//
// BTTS and Clean Sheets are twins: each shows, per fixture team, the rate of a
// binary match outcome (both-teams-scored / kept-a-clean-sheet) in two views —
//   - "donut"  — a two-segment half-donut (Yes green / No grey) + centre %.
//   - "blocks" — a per-team grid of recent results, one green/red box per match.
// They differ only in data source + the per-match predicate, so the shaping is
// shared here (kind = "btts" | "cleanSheet"). Pure + framework-free; the donut
// arc geometry + D3 animation live in <HalfDonut>, the blocks grid is plain JSX.

import type { DonutSegment } from "./donut.js";
import { formTooltip, type FormMatch } from "./form.js";

type Window = "5" | "10" | "Season";
type Venue = "All" | "Home" | "Away";

/** Yes-count out of n games, for one venue + window. */
export interface RateWindow {
  Y: number;
  n: number;
}

/** Match-rate data keyed by full team name, then venue, then window. */
export type RateData = Record<string, Record<Venue, Record<Window, RateWindow>>>;

export type RateKind = "btts" | "cleanSheet";

/** A shaped donut: its two arcs + the centre % text (always shown). */
export interface RateDonut {
  segments: DonutSegment[];
  pct: { value: number; color: string };
}

/** One block in the blocks view: its fill colour + tooltip. */
export interface RateBlock {
  color: string;
  tooltip: string;
}

const GREEN = "#2ecc71";
const GREY = "#888";
const RED = "#e74c3c";

/** Normalise the timeline control to a data key (anything but 10/Season → 5). */
function toWindow(timeline: string | null | undefined): Window {
  return timeline === "10" ? "10" : timeline === "Season" ? "Season" : "5";
}

/** Normalise the venue control (anything but Home/Away → All). */
function toVenue(ha: string | null | undefined): Venue {
  return ha === "Home" ? "Home" : ha === "Away" ? "Away" : "All";
}

/**
 * Shape one team's donut for the venue + window. The arcs use the raw
 * percentage; the centre text is rounded — kept verbatim from the legacy
 * renderer. Missing data falls back to 0/1 (an empty donut).
 */
export function shapeRateDonut(
  data: RateData,
  team: string,
  ha: string | null | undefined,
  timeline: string | null | undefined
): RateDonut {
  const d = data[team]?.[toVenue(ha)]?.[toWindow(timeline)] ?? { Y: 0, n: 1 };
  const yPct = (d.Y / d.n) * 100;
  return {
    segments: [
      { value: yPct, color: GREEN, key: "Y" },
      { value: 100 - yPct, color: GREY, key: "N" },
    ],
    pct: { value: Math.round(yPct), color: GREEN },
  };
}

/** Whether a match satisfies the rate for `team` (kind-specific predicate). */
function passes(m: FormMatch, team: string, kind: RateKind): boolean {
  const g = m.score.split("–").map(Number);
  if (g.length !== 2) return false;
  const [home, away] = g as [number, number];
  if (kind === "btts") return home > 0 && away > 0;
  // cleanSheet — the opponent scored zero.
  return m.home === team ? away === 0 : home === 0;
}

/**
 * Shape one team's blocks view: the last-n matches (venue-filtered), each a
 * green/red box by whether it satisfied the rate. The window is 10 or 5 only
 * (Season → 5), matching the legacy blocks renderer.
 */
export function shapeRateBlocks(
  matches: readonly FormMatch[] | undefined,
  team: string,
  ha: string | null | undefined,
  timeline: string | null | undefined,
  kind: RateKind
): RateBlock[] {
  const n = timeline === "10" ? 10 : 5;
  const venue = toVenue(ha);
  const all = matches ?? [];
  const filtered =
    venue === "Home"
      ? all.filter((m) => m.home === team)
      : venue === "Away"
        ? all.filter((m) => m.away === team)
        : all;
  return filtered.slice(-n).map((m) => ({
    color: passes(m, team, kind) ? GREEN : RED,
    tooltip: formTooltip(m),
  }));
}
